// Hierarchical query-key factory. Centralising the keys lets mutation hooks
// invalidate with surgical precision (`queryKeys.partners.detail(id)`) or
// broad strokes (`queryKeys.partners.all`) without typo-ing literal arrays.
//
// Replaces ~174 `revalidatePath()` calls from the Next.js codebase. The
// 10-15 logical entity groups below collapse the previous path-based
// invalidation strategy into a typed key-based one.

import type { TimeRangeKey } from "@colab/shared";

export const queryKeys = {
  session: ["session"] as const,
  auth: {
    me: () => ["auth", "me"] as const,
  },
  partners: {
    all: ["partners"] as const,
    list: () => ["partners", "list"] as const,
    detail: (id: string) => ["partners", "detail", id] as const,
    deals: (id: string) => ["partners", "detail", id, "deals"] as const,
    onboarding: (id: string) => ["partners", "detail", id, "onboarding"] as const,
    users: (id: string) => ["partners", "detail", id, "users"] as const,
    hubspotCandidates: (q?: string) =>
      q
        ? (["partners", "hubspot-candidates", q] as const)
        : (["partners", "hubspot-candidates"] as const),
    // Internal-only Notion "Integrations KB" link + live page. `integrationKb`
    // nests under detail(id) so a partner-level invalidate sweeps it;
    // candidates are a flat search cache keyed by query.
    integrationKb: (id: string) =>
      ["partners", "detail", id, "integration-kb"] as const,
    integrationKbCandidates: (q?: string) =>
      q
        ? (["partners", "integration-kb-candidates", q] as const)
        : (["partners", "integration-kb-candidates"] as const),
  },
  deals: {
    all: ["deals"] as const,
    list: () => ["deals", "list"] as const,
    detail: (id: string) => ["deals", "detail", id] as const,
    comments: (id: string) => ["deals", "detail", id, "comments"] as const,
    collaborators: (id: string) =>
      ["deals", "detail", id, "collaborators"] as const,
  },
  forms: {
    all: ["forms"] as const,
    list: () => ["forms", "list"] as const,
    detail: (slug: string) => ["forms", "detail", slug] as const,
    submissions: (slug: string) => ["forms", "detail", slug, "submissions"] as const,
  },
  adminForms: {
    all: ["admin-forms"] as const,
    list: () => ["admin-forms", "list"] as const,
    detail: (id: string) => ["admin-forms", "detail", id] as const,
  },
  journeys: {
    all: ["journeys"] as const,
    list: () => ["journeys", "list"] as const,
    detail: (id: string) => ["journeys", "detail", id] as const,
    // Sub-bucket 14 — admin assignments-for-journey list. Lives under the
    // detail subtree so a journey-level invalidate sweeps the assignments
    // cache too. Partner-side counterpart is queryKeys.portal.journeys.*.
    assignmentsForJourney: (id: string) =>
      ["journeys", "detail", id, "assignments"] as const,
  },
  mdf: {
    all: ["mdf"] as const,
    requests: () => ["mdf", "requests"] as const,
    request: (id: string) => ["mdf", "requests", id] as const,
    // Bucket 9 — drafts have their own subtree because the auto-save flow
    // refetches them frequently; keeping the key distinct from `request(id)`
    // means an unrelated `requests()` invalidate doesn't churn the form.
    draft: (id: string) => ["mdf", "drafts", id] as const,
    requestComments: (id: string) =>
      ["mdf", "requests", id, "comments"] as const,
    // Bucket 10 — admin fund + allocation surface. `funds()` is the admin
    // index (cards on /admin/mdf); `fund(id)` is the fund-detail page which
    // includes the allocation list + recent requests for that fund. Kept
    // separate from `requests()` (the cross-fund queue surface) so an admin
    // edit to one fund doesn't churn the queue's cache.
    funds: () => ["mdf", "funds"] as const,
    fund: (id: string) => ["mdf", "funds", id] as const,
    // Sub-bucket 15 — fund-level ROI aggregate (committed / spent / revenue /
    // breakdown). Distinct from `fund(id)` because the ROI tab loads on
    // demand only (when the user clicks the tab) and an allocation edit
    // shouldn't invalidate the ROI cache — ROI is purely request- and
    // deals-driven.
    fundRoi: (id: string) => ["mdf", "funds", id, "roi"] as const,
    // Sub-bucket 3 — partner-side active allocations (the /portal/mdf/new
    // chooser source and the list-page aggregate row). Distinct from
    // `funds()` (admin index) so partner reads don't get churned by admin
    // fund mutations.
    allocations: () => ["mdf", "allocations"] as const,
  },
  // Phase 7 sub-bucket 13 — `views(id)` backs the detail-page Views tab
  // analytics aggregate; `folders()` backs the Move dialog's flat folder
  // picker (separate from `list()` because the folder picker is reused
  // across detail pages and shouldn't churn on every list filter change).
  content: {
    all: ["content"] as const,
    list: () => ["content", "list"] as const,
    detail: (id: string) => ["content", "detail", id] as const,
    views: (id: string) => ["content", "detail", id, "views"] as const,
    folders: () => ["content", "folders"] as const,
  },
  // Bucket 14 — admin goals (partner_goals + partner_goal_targets). `forPartner`
  // intentionally nests under partners.detail(id) so a goal-write that touches a
  // single partner cascades to the partner-detail page without a separate
  // invalidate. `targetsForGoal` nests under `goals.detail` so a target write
  // doesn't churn the whole list cache. Goal queries (list/detail/targets) are
  // not implemented yet — Phase 7 wires the goals admin pages. Mutation hooks
  // ship now so the keys exist for downstream invalidation.
  goals: {
    all: ["goals"] as const,
    list: () => ["goals", "list"] as const,
    detail: (id: string) => ["goals", "detail", id] as const,
    targetsForGoal: (goalId: string) =>
      ["goals", "detail", goalId, "targets"] as const,
    forPartner: (partnerId: string) =>
      ["partners", "detail", partnerId, "goals"] as const,
  },
  // Bucket 7 — admin tiers editor + assignments. Hierarchical so
  // mutations on a specific program (tier reorder, matrix-cell flip, etc.)
  // can target `tiers.program(id)` while broader writes can flush
  // `tiers.all`. Partner-tier assignment mutations also invalidate
  // `partners.detail(id)` + `partners.list()` since the tier badge ripples
  // across the partners feature.
  tiers: {
    all: ["tiers"] as const,
    programs: {
      list: () => ["tiers", "programs", "list"] as const,
    },
    program: (id: string) => ["tiers", "program", id] as const,
    assignmentsForProgram: (programId: string) =>
      ["tiers", "program", programId, "assignments"] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    inbox: () => ["notifications", "inbox"] as const,
  },
  // Bucket 8 — admin submissions accept/decline. Phase 7 sub-bucket 17
  // wires the SPA queue + drawer consumer: list() is the bare prefix; the
  // hook appends every filter dimension (status, formId, partnerId, sort,
  // dir, filterHash) onto it so distinct filter states cache independently.
  // Mutations invalidate the bare key — TanStack prefix matching sweeps
  // every filter-tupled cache. `aggregate()` backs the 4 metric cards on
  // the queue page and is invalidated by accept + decline so the counts
  // refresh after a review.
  submissions: {
    all: ["submissions"] as const,
    list: () => ["submissions", "list"] as const,
    detail: (id: string) => ["submissions", "detail", id] as const,
    aggregate: () => ["submissions", "aggregate"] as const,
  },
  // Admin queued-leads surface (pending_leads dead-letter queue). `list()` is
  // the bare prefix; the hook appends the status filter onto it. The manual
  // "Retry now" drain invalidates the bare `all` key so every filtered cache
  // refreshes via TanStack prefix matching.
  pendingLeads: {
    all: ["pendingLeads"] as const,
    list: () => ["pendingLeads", "list"] as const,
  },
  // Bucket 8 — admin portal-content CMS + FAQ. The public* keys mirror the
  // portal-side reads (welcome banner, level-up widget, support FAQ list) so
  // an admin edit flushes the partner-facing cache. Those portal reads are
  // still SSR-rendered today; invalidations are no-ops until Phase 7 ports
  // the portal SPA. TODO(phase-7): consumed by SPA portal migration.
  portalContent: {
    all: ["portalContent"] as const,
    adminList: () => ["portalContent", "admin", "list"] as const,
    detail: (id: string) => ["portalContent", "detail", id] as const,
    publicAll: () => ["portalContent", "public"] as const,
    faqAdmin: () => ["portalContent", "faq", "admin"] as const,
    faqPublic: () => ["portalContent", "faq", "public"] as const,
  },
  settings: {
    all: ["settings"] as const,
    roles: () => ["settings", "roles"] as const,
    role: (id: string) => ["settings", "roles", id] as const,
    audit: () => ["settings", "audit"] as const,
    announcements: () => ["settings", "announcements"] as const,
    integrations: () => ["settings", "integrations"] as const,
    hubspotPartners: () => ["settings", "integrations", "hubspot-partners"] as const,
    hubspotPartnerFieldMap: () =>
      ["settings", "integrations", "hubspot", "partner-field-map"] as const,
    hubspotDiscoverablePartnerProperties: () =>
      ["settings", "integrations", "hubspot", "partner-properties"] as const,
    notificationRules: () => ["settings", "notifications", "rules"] as const,
    // Sub-bucket 19 — 30-day notification analytics aggregate that backs the
    // InsightsBar on /admin/settings/notifications. Invalidated by the four
    // notification-rule mutations (rule changes shift the dispatcher's
    // routing decisions, so the next refresh should pick up the trend).
    notificationInsights: () =>
      ["settings", "notifications", "insights"] as const,
    segments: () => ["settings", "segments"] as const,
    segment: (id: string) => ["settings", "segments", id] as const,
    invites: () => ["settings", "invites"] as const,
  },
  users: {
    all: ["users"] as const,
    internal: () => ["users", "internal"] as const,
    // User Manager (/admin/users). `team` = internal teammates + pending
    // invites; `partnerContacts` = partner users across all partners (or one
    // partner when filtered).
    team: () => ["users", "team"] as const,
    partnerContacts: (partnerId?: string) =>
      partnerId
        ? (["users", "partner-contacts", partnerId] as const)
        : (["users", "partner-contacts"] as const),
  },
  // Phase 7 sub-bucket 9 — admin section reads. Opens the admin slice;
  // sub-buckets 10–17 extend this namespace with partners/forms/journeys/
  // mdf/etc. The dashboard endpoint aggregates 8 legacy Supabase reads (4
  // counters + 2 attribution lists + leaderboard + MDF open-funds) into one
  // GET, so the single key here is intentional.
  admin: {
    all: ["admin"] as const,
    dashboard: () => ["admin", "dashboard"] as const,
    // Phase 7 sub-bucket 18 — workspace-aggregate analytics. Single GET
    // backs /admin/analytics; distinct from the per-partner
    // `portal.analytics.*` namespace.
    analytics: () => ["admin", "analytics"] as const,
    // Phase 7 sub-bucket 10 — admin partners list + detail. Distinct
    // namespace from `queryKeys.partners.*` (the Phase 6 mutation-side
    // keys) because the admin list endpoint augments rows with
    // deal_count / intro_count aggregates that the Phase 6 raw
    // `partners.list()` doesn't carry. Partner-write mutations
    // invalidate both namespaces (cross-key invalidation) so the admin
    // list, partner-detail overview, and dashboard leaderboard all
    // refresh after a write.
    partners: {
      all: ["admin", "partners"] as const,
      list: () => ["admin", "partners", "list"] as const,
      overview: (id: string) => ["admin", "partners", "detail", id, "overview"] as const,
      deals: (id: string) => ["admin", "partners", "detail", id, "deals"] as const,
    },
    // 0091 — marketplace listing-change review queue + link-picker options.
    // Decision mutations invalidate `listingChanges.all` (prefix sweep across
    // every status filter + open drawer detail).
    listingChanges: {
      all: ["admin", "listing-changes"] as const,
      list: (status: string) => ["admin", "listing-changes", "list", status] as const,
      detail: (id: string) => ["admin", "listing-changes", "detail", id] as const,
    },
    marketplaceListings: () => ["admin", "marketplace-listings"] as const,
    // Phase 7 sub-bucket 16 — admin deals Kanban + drawer. Distinct from
    // `queryKeys.deals.*` (the Phase 6 mutation-side keys) because the
    // admin Kanban response shape (stages + grouped) and the drawer payload
    // (deal + attributions + account + collaborators + unboardedPartners +
    // owner embeds) augment the raw partner-side reads. `kanban(showClosed)`
    // is discriminated so the "Show closed" toggle doesn't churn the
    // default cache. Mutations on the bucket-13 surface (link/unlink
    // account + add/remove collaborator) cross-invalidate both this
    // namespace AND the Phase 6 `deals.collaborators(id)` cache.
    deals: {
      all: ["admin", "deals"] as const,
      kanban: (showClosed: boolean) =>
        ["admin", "deals", "kanban", showClosed] as const,
      detail: (hubspotDealId: string) =>
        ["admin", "deals", "detail", hubspotDealId] as const,
      eligibleCollaborators: (hubspotDealId: string) =>
        ["admin", "deals", "detail", hubspotDealId, "eligible-collaborators"] as const,
    },
  },
  // Phase 7 sub-bucket 16 — Saved Views CRUD. Per entity_type, the views
  // namespace is small (~5–10 rows per entity) so we hoist a single
  // `list(entityType)` key per surface. `detail(id)` is reserved for the
  // edit-existing-view flows; today the Tabs render the row inline from
  // the list cache so `detail` doesn't have a consumer yet (mutation hooks
  // invalidate `list(entityType)` only).
  views: {
    all: ["views"] as const,
    list: (entityType: string) => ["views", "list", entityType] as const,
    detail: (id: string) => ["views", "detail", id] as const,
  },
  portal: {
    all: ["portal"] as const,
    profile: () => ["portal", "profile"] as const,
    onboarding: () => ["portal", "onboarding"] as const,
    // Phase 7 sub-bucket 1 — partner dashboard reads. Each key backs one of
    // the 7 GET endpoints added to apps/api: /portal/partner, /deal-summary,
    // /portal-content, /faq, /goals, /tier-program, /announcements/active.
    // The hooks live alongside the Phase 6 mutation hooks in
    // apps/web/src/features/portal/.
    partner: () => ["portal", "partner"] as const,
    dealSummary: () => ["portal", "deal-summary"] as const,
    content: () => ["portal", "content"] as const,
    faq: (limit?: number) =>
      typeof limit === "number" ? (["portal", "faq", limit] as const) : (["portal", "faq"] as const),
    goals: () => ["portal", "goals"] as const,
    tierProgram: () => ["portal", "tier-program"] as const,
    announcement: () => ["portal", "announcement"] as const,
    // 0091 — marketplace "My Listing". One key backs both the page and the
    // top-nav gate (see features/portal/listing/use-portal-listing.ts).
    listing: () => ["portal", "listing"] as const,
    // Bucket 12 — partner-side journey + task-progress. Keyed by
    // assignment_id (the partner-visible unit: one assignment per
    // partner-journey pair). Bucket-11 admin marker closures invalidate
    // `portal.journeys.all` hierarchically because the admin doesn't know
    // which assignment-keyed detail entry a partner has cached.
    journeys: {
      all: ["portal", "journeys"] as const,
      list: () => ["portal", "journeys", "list"] as const,
      detail: (assignmentId: string) =>
        ["portal", "journeys", "detail", assignmentId] as const,
      // Sub-bucket 4 — per-task surfaces. Keyed under detail(progressId) so
      // a comment or attachment write can invalidate just the affected task
      // without churning the whole journey-detail cache.
      taskComments: (progressId: string) =>
        ["portal", "journeys", "task", progressId, "comments"] as const,
      taskAttachments: (progressId: string) =>
        ["portal", "journeys", "task", progressId, "attachments"] as const,
    },
    // Bucket 12 — partner-side cross-journey task aggregates. No SPA
    // consumer today; Phase 7 will wire `portal.tasks.list()` to the
    // dashboard active-task feed and `portal.tasks.openCount()` to the
    // top-nav unread/open-task badge. Pre-built here so bucket 12 hooks
    // can invalidate them on day one; bucket 13+ inherits the matrix.
    tasks: {
      all: ["portal", "tasks"] as const,
      list: () => ["portal", "tasks", "list"] as const,
      openCount: () => ["portal", "tasks", "open-count"] as const,
    },
    // Phase 7 sub-bucket 2a — partner-side forms runtime (drafts + multi-file
    // attachments + auto-save). The `detail(slug)` key is the page-level
    // cache (form + current draft); `draft(submissionId)` is used for re-
    // reads after page refresh; `submission(submissionId)` backs the
    // post-submit confirmation page.
    forms: {
      all: ["portal", "forms"] as const,
      list: () => ["portal", "forms", "list"] as const,
      detail: (slug: string) =>
        ["portal", "forms", "detail", slug] as const,
      draft: (submissionId: string) =>
        ["portal", "forms", "draft", submissionId] as const,
      submission: (submissionId: string) =>
        ["portal", "forms", "submission", submissionId] as const,
    },
    // Phase 7 sub-bucket 5 — partner-portal deals rich rebuild. Separate
    // from the legacy `deals.*` namespace (bucket 13 admin) because the
    // partner-side and admin-side reads invalidate independently. The
    // existing `deals.comments(id)` key is kept as-is to avoid breaking
    // the bucket-3 optimistic comment append hook; only the partner-side
    // list/detail/timeline reads live here.
    deals: {
      all: ["portal", "deals"] as const,
      list: () => ["portal", "deals", "list"] as const,
      detail: (id: string) => ["portal", "deals", "detail", id] as const,
      timeline: (id: string) =>
        ["portal", "deals", "detail", id, "timeline"] as const,
    },
    // Phase 7 sub-bucket 6 — partner analytics reads. Each key is
    // range-discriminated so a TimeRangeSelector change refetches all four
    // with fresh keys. The shared analytics components hoisted in this
    // bucket are reused by admin sub-bucket 10; admin mounts the same view
    // under a separate `admin.analytics.*` namespace (keys added then).
    analytics: {
      all: ["portal", "analytics"] as const,
      pipeline: (range: TimeRangeKey) =>
        ["portal", "analytics", "pipeline", range] as const,
      contentEngagement: (range: TimeRangeKey) =>
        ["portal", "analytics", "content-engagement", range] as const,
      notifications: (range: TimeRangeKey) =>
        ["portal", "analytics", "notifications", range] as const,
      activity: (range: TimeRangeKey) =>
        ["portal", "analytics", "activity", range] as const,
    },
    // Phase 7 sub-bucket 7 — notification preferences (32-cell matrix).
    // Single key; the GET response is the pre-realized matrix and cell
    // toggles patch a single entry in place via Pattern C optimism.
    preferences: () => ["portal", "preferences"] as const,
    // Phase 7 sub-bucket 8 — partner-side resources library.
    // `list(folder)` keys discriminate root (`null`) from each folder
    // (UUID) so navigating between folders doesn't churn the root cache.
    // `detail(id)` backs the breadcrumb folder-title fetch.
    // `signedAssetUrl(id)` backs the lazy file-download fetch — 50 min
    // staleTime aligns with the 1h TTL on the server-minted URL.
    resources: {
      all: ["portal", "resources"] as const,
      list: (folderId: string | null) =>
        ["portal", "resources", "list", folderId] as const,
      detail: (id: string) => ["portal", "resources", "detail", id] as const,
    },
    signedAssetUrl: (assetId: string) =>
      ["portal", "signed-asset-url", assetId] as const,
  },
} as const;
